/**
 * Avangard Malz — Gelsenkirchen, Bremen, Koblenz and Lechfeld, Germany.
 *
 * Colour is published in EBC on the product range page; `colorEbc` is the
 * mid-point of the published band and the °L figure is the conversion
 * `°L = (EBC + 1.2) / 2.65` rounded to one decimal.
 *
 * The Pilsener (two-row spring) spec sheet is the one complete analysis in
 * the range: extract as a minimum, moisture as a maximum, colour as a band
 * and diastatic power as a Windisch-Kolbach minimum. That row stores those
 * figures and converts DP with `°Lintner = (°WK + 16) / 3.5`, and is
 * `spec-sheet`. Every other SKU publishes colour and a use-description; the
 * rest of the numbers are class-typical and those rows are `representative`.
 *
 * Only the Pilsener row carries a `url`, because that is the one per-product
 * document on the maker's own domain. The range page is a family listing.
 *
 * @see https://www.avangard-malz.de/en/products/
 */

import type { Fermentable } from '../../schema/fermentable';

/** Avangard Malz malts. @see Fermentable */
export const avangardFermentables: readonly Fermentable[] = [
  {
    id: 'avangard-pilsener',
    name: 'Pilsener Malt',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 67,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row spring pilsener from German, French, Danish and English barley. Pale enough for helles and Kölsch, and enzyme-rich enough to be the whole grist under anything hop-forward.',
    tags: ['base', 'lager', 'german', 'pilsner'],
    url: 'https://www.avangard-malz.de/upload/iblock/1cb/1cbb170b18740b3d58e8786cf14b3e8c.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'avangard-pilsener-6-row',
    name: 'Pilsener Malt 6-Row Winter',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Six-row winter pilsener sold for adjunct mashes: the extra amylase is the point. Use it when corn, rice or unmalted grain makes up a real share of the grist.',
    tags: ['base', 'lager', 'german', 'pilsner', 'six-row', 'high-enzyme', 'adjunct'],
    provenance: 'representative',
  },
  {
    id: 'avangard-pilsener-2-row-winter',
    name: 'Pilsener Malt 2-Row Winter',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 67,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row winter pilsener, the same color as the spring grade and the same job: a pale, clean lager base. Winter barley is the crop, not a flavor claim.',
    tags: ['base', 'lager', 'german', 'pilsner', 'winter'],
    provenance: 'representative',
  },
  {
    id: 'avangard-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned warmer than the pilsener, for pale ale and IPA. A gold wort with enough enzyme in reserve to carry crystal and roasted malt on top of it.',
    tags: ['base', 'ale', 'german', 'pale-ale'],
    provenance: 'representative',
  },
  {
    id: 'avangard-vienna',
    name: 'Vienna Malt',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 4.2,
    colorEbc: 10,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Vienna-kilned malt that still converts itself. Toasted-bread sweetness and a gold-to-amber cast for Vienna lager, Märzen and anything that wants more malt than pale ale gives.',
    tags: ['base', 'kilned', 'german', 'vienna'],
    provenance: 'representative',
  },
  {
    id: 'avangard-munich',
    name: 'Munich Malt',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 9.3,
    colorEbc: 23.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Munich malt with a wide published color band, landing around a typical dark-gold Munich. Bread-crust maltiness for dunkel, bock and festbier, and enough enzyme to be a large share of the grist.',
    tags: ['base', 'kilned', 'german', 'munich'],
    provenance: 'representative',
  },
  {
    id: 'avangard-light-caramel',
    name: 'Light Caramel Malt',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 7.1,
    colorEbc: 17.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale caramel for body and a lower finishing gravity, not for color. Five to fifteen percent plump a pils or pale ale without turning it amber.',
    tags: ['crystal', 'caramel', 'german', 'light', 'body'],
    provenance: 'representative',
  },
  {
    id: 'avangard-dark-caramel',
    name: 'Dark Caramel Malt',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 18.4,
    colorEbc: 47.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker caramel grade: color, body and a clean toffee maltiness. Five to ten percent is the usual dose in amber ale, altbier and Märzen.',
    tags: ['crystal', 'caramel', 'german'],
    provenance: 'representative',
  },
  {
    id: 'avangard-wheat',
    name: 'Wheat Malt',
    maker: 'Avangard Malz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malted wheat for weissbier and Kölsch. High extract, high protein, and the usual foam and haze; rice hulls help above about 50 %.',
    tags: ['base', 'wheat', 'german', 'head-retention'],
    provenance: 'representative',
  },
];
